import gettingStartedImg from "../Assets/Blogs/getting-started.jpg";
import performanceImg from "../Assets/Blogs/performance.jpg";
import expoVsBareImg from "../Assets/Blogs/expo-vs-bare.jpg";
import componentsImg from "../Assets/Blogs/components.jpg";
import navigationImg from "../Assets/Blogs/navigation.jpg";
import debuggingImg from "../Assets/Blogs/debugging.jpg";
import nextjsAppRouterImg from "../Assets/Blogs/nextjs-app-router.jpg";
import nextjsSsrSeoImg from "../Assets/Blogs/nextjs-ssr-seo.jpg";
import reactHooksImg from "../Assets/Blogs/react-hooks.jpg";
import reactStateImg from "../Assets/Blogs/react-state.jpg";
import androidDevImg from "../Assets/Blogs/android-development.jpg";
import androidPlayStoreImg from "../Assets/Blogs/android-play-store.jpg";
import iosDevImg from "../Assets/Blogs/ios-development.jpg";
import iosAppStoreImg from "../Assets/Blogs/ios-app-store.jpg";

export const blogs = [
  {
    slug: "getting-started-react-native-2026",
    title: "Getting Started with React Native in 2026: A Practical Guide",
    excerpt:
      "A hands-on walkthrough for setting up your first React Native app, choosing the right tooling, and shipping to both iOS and Android.",
    date: "2026-03-10",
    readTime: "8 min read",
    tags: ["React Native", "Expo", "Beginner"],
    image: gettingStartedImg,
    gradient: "linear-gradient(135deg, #61dafb 0%, #a855f7 100%)",
    content: [
      {
        type: "paragraph",
        text: "React Native lets you build real mobile apps using JavaScript and React. If you already know React for the web, you're halfway there — but mobile brings its own layout model, navigation patterns, and platform quirks worth understanding upfront.",
      },
      {
        type: "heading",
        text: "Choose Your Starting Point",
      },
      {
        type: "paragraph",
        text: "In 2026, most new projects start with Expo. It handles native builds, over-the-air updates, and a huge set of pre-built modules so you can focus on product logic instead of Xcode and Gradle configuration. Bare React Native (without Expo) still makes sense for apps that need deep native customization from day one.",
      },
      {
        type: "code",
        language: "bash",
        text: "npx create-expo-app@latest MyApp\ncd MyApp\nnpx expo start",
      },
      {
        type: "heading",
        text: "Core Concepts to Learn First",
      },
      {
        type: "list",
        items: [
          "View, Text, and ScrollView — the building blocks of every screen",
          "StyleSheet and Flexbox — mobile layout is flex-first, not CSS-grid-first",
          "Platform-specific code with Platform.OS and .ios / .android file extensions",
          "SafeAreaView and status bar handling for notched devices",
          "React Navigation for stack, tab, and drawer flows",
        ],
      },
      {
        type: "heading",
        text: "Your First Screen",
      },
      {
        type: "paragraph",
        text: "Keep your first screen simple. Render a list, fetch data from an API, and navigate to a detail view. That single flow teaches you 80% of what you'll use daily — components, state, effects, and navigation.",
      },
      {
        type: "code",
        language: "javascript",
        text: `import { View, Text, FlatList, StyleSheet } from "react-native";

export default function HomeScreen() {
  const items = ["React Native", "Expo", "TypeScript"];

  return (
    <View style={styles.container}>
      <Text style={styles.title}>My First App</Text>
      <FlatList
        data={items}
        keyExtractor={(item) => item}
        renderItem={({ item }) => <Text style={styles.item}>{item}</Text>}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 24, paddingTop: 60 },
  title: { fontSize: 24, fontWeight: "700", marginBottom: 16 },
  item: { fontSize: 16, paddingVertical: 12 },
});`,
      },
      {
        type: "paragraph",
        text: "Ship early on a real device. The simulator is useful, but touch targets, scroll physics, and keyboard behavior only feel right on hardware. Test on both iOS and Android before you call a feature done.",
      },
    ],
  },
  {
    slug: "react-native-performance-tips",
    title: "React Native Performance Tips Every Developer Should Know",
    excerpt:
      "Practical techniques to keep your React Native apps fast — from list rendering and memoization to image optimization and native thread work.",
    date: "2026-02-22",
    readTime: "10 min read",
    tags: ["React Native", "Performance"],
    image: performanceImg,
    gradient: "linear-gradient(135deg, #22d3ee 0%, #6366f1 100%)",
    content: [
      {
        type: "paragraph",
        text: "Users expect native-level smoothness. A few bad patterns — unnecessary re-renders, heavy work on the JS thread, or unoptimized images — can make an otherwise solid app feel sluggish. The good news: most performance wins are straightforward once you know what to look for.",
      },
      {
        type: "heading",
        text: "1. Optimize Your Lists",
      },
      {
        type: "paragraph",
        text: "FlatList and SectionList are built for large datasets, but only if you configure them correctly. Always provide a stable keyExtractor, avoid inline renderItem functions when possible, and tune windowSize and maxToRenderPerBatch for your use case.",
      },
      {
        type: "code",
        language: "javascript",
        text: `<FlatList
  data={posts}
  keyExtractor={(item) => item.id}
  renderItem={renderPost}
  getItemLayout={(_, index) => ({
    length: ITEM_HEIGHT,
    offset: ITEM_HEIGHT * index,
    index,
  })}
  removeClippedSubviews
  windowSize={5}
/>`,
      },
      {
        type: "heading",
        text: "2. Memoize Expensive Components",
      },
      {
        type: "paragraph",
        text: "React.memo, useMemo, and useCallback aren't free — but they pay off when child components re-render on every parent update. Profile first with React DevTools or Flipper, then memoize components that render often with stable props.",
      },
      {
        type: "heading",
        text: "3. Move Heavy Work Off the JS Thread",
      },
      {
        type: "list",
        items: [
          "Use react-native-reanimated for animations that must run at 60fps",
          "Defer non-urgent work with InteractionManager.runAfterInteractions",
          "Parse large JSON or process images in a background thread or native module",
          "Avoid synchronous AsyncStorage reads on the critical render path",
        ],
      },
      {
        type: "heading",
        text: "4. Images Matter More Than You Think",
      },
      {
        type: "paragraph",
        text: "Serve appropriately sized images, use WebP where supported, and set explicit width and height to prevent layout thrashing. Libraries like react-native-fast-image add caching and priority control that the default Image component lacks.",
      },
      {
        type: "paragraph",
        text: "Measure before and after every optimization. The Hermes engine and the New Architecture (Fabric + TurboModules) improve baseline performance, but disciplined component design still makes the biggest difference in real apps.",
      },
    ],
  },
  {
    slug: "expo-vs-bare-react-native",
    title: "Expo vs Bare React Native: Which Should You Choose?",
    excerpt:
      "A clear comparison of Expo and bare React Native workflows — when each makes sense, and how to migrate between them if your needs change.",
    date: "2026-02-05",
    readTime: "7 min read",
    tags: ["React Native", "Expo", "Architecture"],
    image: expoVsBareImg,
    gradient: "linear-gradient(135deg, #f472b6 0%, #a855f7 100%)",
    content: [
      {
        type: "paragraph",
        text: "The Expo vs bare React Native debate used to be much simpler — and much more polarized. Today, Expo supports custom native code, EAS Build, and config plugins, which means the choice is less about capability and more about team workflow and project constraints.",
      },
      {
        type: "heading",
        text: "When Expo Is the Right Call",
      },
      {
        type: "list",
        items: [
          "You're starting a new app and want to ship features fast",
          "Your team is JavaScript-heavy with limited native mobile experience",
          "You need OTA updates, push notifications, and camera/location without writing native code",
          "You want managed builds via EAS without maintaining your own CI for native binaries",
        ],
      },
      {
        type: "heading",
        text: "When Bare React Native Makes Sense",
      },
      {
        type: "list",
        items: [
          "You depend on native SDKs that lack Expo config plugin support",
          "You need fine-grained control over native project files and build pipelines",
          "You're integrating with an existing brownfield iOS/Android codebase",
          "Your app has unusual native threading or background execution requirements",
        ],
      },
      {
        type: "heading",
        text: "The Middle Path: Expo with Prebuild",
      },
      {
        type: "paragraph",
        text: "Expo's prebuild command generates ios/ and android/ directories from your app config. You can eject selectively, add custom native modules, and still use EAS for builds. Many production apps run this way — it's not truly 'managed' nor fully bare, but it combines the best of both.",
      },
      {
        type: "code",
        language: "bash",
        text: "npx expo prebuild\nnpx expo run:ios\nnpx expo run:android",
      },
      {
        type: "paragraph",
        text: "My rule of thumb: start with Expo unless you have a documented native requirement on day one. Migrating from Expo to bare is easier than it used to be, and premature native complexity slows down every team I've worked with.",
      },
    ],
  },
  {
    slug: "reusable-components-react-native",
    title: "Building Reusable Components in React Native",
    excerpt:
      "How to design a component library that scales — consistent APIs, theming, accessibility, and patterns that work across iOS and Android.",
    date: "2026-01-18",
    readTime: "9 min read",
    tags: ["React Native", "Components", "Architecture"],
    image: componentsImg,
    gradient: "linear-gradient(135deg, #34d399 0%, #22d3ee 100%)",
    content: [
      {
        type: "paragraph",
        text: "Every React Native app eventually needs a shared component layer — buttons, inputs, cards, modals, and typography that look consistent everywhere. Without intentional design, each screen invents its own styles and you end up with five different primary buttons.",
      },
      {
        type: "heading",
        text: "Start With a Design Token Layer",
      },
      {
        type: "paragraph",
        text: "Define colors, spacing, font sizes, and border radii in one place. Pass them through a ThemeContext or a simple theme object. Components should read from tokens, not hardcoded hex values.",
      },
      {
        type: "code",
        language: "javascript",
        text: `export const theme = {
  colors: {
    primary: "#a855f7",
    text: "#f0f0f8",
    textMuted: "#9898b0",
    surface: "rgba(255,255,255,0.06)",
    border: "rgba(255,255,255,0.08)",
  },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
  radius: { sm: 8, md: 16, full: 9999 },
};`,
      },
      {
        type: "heading",
        text: "Compose Small Primitives",
      },
      {
        type: "paragraph",
        text: "Build Box, Text, Button, and Input as low-level primitives. Higher-level components like ProductCard or LoginForm compose these primitives. This mirrors how design systems work on the web and keeps refactors manageable.",
      },
      {
        type: "heading",
        text: "Handle Platform Differences Gracefully",
      },
      {
        type: "list",
        items: [
          "Use Platform.select for one-off style differences",
          "Split into Component.ios.tsx and Component.android.tsx only when logic diverges significantly",
          "Test hitSlop and accessibilityLabel on every interactive element",
          "Respect font scaling — avoid locking fontSize when users enable large text",
        ],
      },
      {
        type: "paragraph",
        text: "Document each component's props with TypeScript interfaces and a short usage example. Future you (and your teammates) will thank you when the app has 40+ screens.",
      },
    ],
  },
  {
    slug: "react-native-navigation-guide",
    title: "React Native Navigation: From Basics to Best Practices",
    excerpt:
      "Master stack, tab, and nested navigators with React Navigation — plus deep linking, type-safe routes, and common pitfalls to avoid.",
    date: "2026-01-02",
    readTime: "11 min read",
    tags: ["React Native", "Navigation"],
    image: navigationImg,
    gradient: "linear-gradient(135deg, #fb923c 0%, #ef4444 100%)",
    content: [
      {
        type: "paragraph",
        text: "Navigation is the backbone of any mobile app. React Navigation is the de facto standard for React Native, offering stack, tab, drawer, and native-stack navigators that feel natural on both platforms.",
      },
      {
        type: "heading",
        text: "Pick the Right Navigator",
      },
      {
        type: "list",
        items: [
          "Native Stack — best default for screen-to-screen flows; uses native transitions",
          "Bottom Tabs — primary app sections (Home, Search, Profile)",
          "Material Top Tabs — swipeable content within a section",
          "Drawer — secondary navigation or admin-style apps",
        ],
      },
      {
        type: "heading",
        text: "Nested Navigators Done Right",
      },
      {
        type: "paragraph",
        text: "A typical app nests a stack inside each tab. The tab navigator owns the bottom bar; each tab has its own stack so users can push detail screens without losing tab context.",
      },
      {
        type: "code",
        language: "javascript",
        text: `const HomeStack = createNativeStackNavigator();

function HomeNavigator() {
  return (
    <HomeStack.Navigator>
      <HomeStack.Screen name="Feed" component={FeedScreen} />
      <HomeStack.Screen name="PostDetail" component={PostDetailScreen} />
    </HomeStack.Navigator>
  );
}

const Tab = createBottomTabNavigator();

export default function AppNavigator() {
  return (
    <Tab.Navigator>
      <Tab.Screen name="Home" component={HomeNavigator} options={{ headerShown: false }} />
      <Tab.Screen name="Settings" component={SettingsScreen} />
    </Tab.Navigator>
  );
}`,
      },
      {
        type: "heading",
        text: "Type-Safe Navigation with TypeScript",
      },
      {
        type: "paragraph",
        text: "Define a RootStackParamList and use it with useNavigation and useRoute hooks. This catches invalid route names and missing params at compile time instead of at runtime when a user taps a broken link.",
      },
      {
        type: "heading",
        text: "Deep Linking",
      },
      {
        type: "paragraph",
        text: "Configure linking in your NavigationContainer so URLs like myapp://post/123 open the right screen. This is essential for push notification taps, email links, and marketing campaigns. Test deep links on cold start — that's when most bugs appear.",
      },
    ],
  },
  {
    slug: "debugging-react-native-apps",
    title: "Debugging React Native Apps Like a Pro",
    excerpt:
      "From Flipper and React DevTools to native logs and production crash reporting — a complete toolkit for finding and fixing mobile bugs.",
    date: "2025-12-15",
    readTime: "8 min read",
    tags: ["React Native", "Debugging", "DevTools"],
    image: debuggingImg,
    gradient: "linear-gradient(135deg, #818cf8 0%, #c084fc 100%)",
    content: [
      {
        type: "paragraph",
        text: "Mobile bugs are harder to reproduce than web bugs — different devices, OS versions, network conditions, and background states all play a role. A structured debugging workflow saves hours of guesswork.",
      },
      {
        type: "heading",
        text: "Development Tools",
      },
      {
        type: "list",
        items: [
          "React Native DevTools — inspect component tree, props, and hooks",
          "Flipper — network inspector, layout inspector, and crash logs",
          "console.log with __DEV__ guards — quick and effective during development",
          "Reactotron — Redux/state debugging and custom command palette",
        ],
      },
      {
        type: "heading",
        text: "Reading Native Logs",
      },
      {
        type: "paragraph",
        text: "Some bugs only surface in native code — permission denials, bridge errors, or native module crashes. Use adb logcat for Android and Xcode console for iOS. Filter by your app's package name to cut through the noise.",
      },
      {
        type: "code",
        language: "bash",
        text: "# Android\nadb logcat *:S ReactNative:V ReactNativeJS:V\n\n# iOS — use Xcode > Open Developer Tool > Console",
      },
      {
        type: "heading",
        text: "Common Bug Categories",
      },
      {
        type: "list",
        items: [
          "White screen — usually a JS exception; check Metro bundler and redbox",
          "Layout issues — inspect with flexbox; remember default flexDirection is column",
          "Network failures — test on real devices; simulators don't catch all SSL/certificate issues",
          "Stale state — check useEffect dependencies and closure bugs in event handlers",
        ],
      },
      {
        type: "heading",
        text: "Production Crash Reporting",
      },
      {
        type: "paragraph",
        text: "Integrate Sentry, Bugsnag, or Firebase Crashlytics before you launch. Symbolicate stack traces, tag releases by build number, and set up alerts for new crash types. You can't fix what you can't see.",
      },
      {
        type: "paragraph",
        text: "Reproduce first, isolate second, fix third. Change one variable at a time. And always test your fix on a physical device — the simulator lies about performance and sometimes about behavior too.",
      },
    ],
  },
  {
    slug: "nextjs-app-router-guide",
    title: "Next.js App Router: A Complete Guide for 2026",
    excerpt:
      "Understand file-based routing, layouts, server components, and data fetching in the modern Next.js App Router.",
    date: "2026-04-02",
    readTime: "10 min read",
    tags: ["Next.js", "React", "Web"],
    image: nextjsAppRouterImg,
    gradient: "linear-gradient(135deg, #000000 0%, #61dafb 100%)",
    content: [
      {
        type: "paragraph",
        text: "Next.js App Router is the default way to build React applications with Next.js 13+. It replaces the older pages/ directory with an app/ directory that uses React Server Components, nested layouts, and a more intuitive file-system routing model.",
      },
      {
        type: "heading",
        text: "File-Based Routing",
      },
      {
        type: "paragraph",
        text: "Every folder inside app/ becomes a route segment. A page.tsx file makes that segment publicly accessible. Layouts wrap child routes and persist across navigation — perfect for navbars, sidebars, and shared UI.",
      },
      {
        type: "code",
        language: "text",
        text: `app/
  layout.tsx        → Root layout
  page.tsx          → /
  about/
    page.tsx        → /about
  blog/
    page.tsx        → /blog
    [slug]/
      page.tsx      → /blog/:slug`,
      },
      {
        type: "heading",
        text: "Server vs Client Components",
      },
      {
        type: "paragraph",
        text: "By default, every component in the App Router is a Server Component. Add 'use client' at the top only when you need interactivity — useState, useEffect, event handlers, or browser APIs. Keep as much as possible on the server for faster loads and smaller bundles.",
      },
      {
        type: "heading",
        text: "Data Fetching",
      },
      {
        type: "list",
        items: [
          "Fetch directly in Server Components — no useEffect needed",
          "Use async/await in page.tsx and layout.tsx",
          "Cache with fetch options or revalidate with ISR",
          "Use Server Actions for form submissions and mutations",
        ],
      },
      {
        type: "paragraph",
        text: "Start with the App Router for every new Next.js project in 2026. The ecosystem has caught up, and the performance benefits of Server Components are hard to ignore.",
      },
    ],
  },
  {
    slug: "nextjs-ssr-seo-best-practices",
    title: "Next.js SSR, SSG & SEO Best Practices",
    excerpt:
      "How to use server-side rendering and static generation in Next.js to build fast, search-engine-friendly web applications.",
    date: "2026-03-28",
    readTime: "9 min read",
    tags: ["Next.js", "SEO", "Web"],
    image: nextjsSsrSeoImg,
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #6366f1 100%)",
    content: [
      {
        type: "paragraph",
        text: "One of Next.js's biggest strengths is flexible rendering. You can statically generate pages at build time, render on each request, or use incremental static regeneration — all in the same project.",
      },
      {
        type: "heading",
        text: "Rendering Strategies",
      },
      {
        type: "list",
        items: [
          "SSG (Static) — pages built at compile time, served from CDN",
          "SSR (Dynamic) — pages rendered per request on the server",
          "ISR — static pages revalidated on a schedule",
          "CSR — client-only rendering for highly interactive sections",
        ],
      },
      {
        type: "heading",
        text: "SEO Essentials in Next.js",
      },
      {
        type: "paragraph",
        text: "Use the Metadata API in App Router to set title, description, Open Graph, and Twitter cards per page. Server-rendered HTML means crawlers see your content immediately — no waiting for JavaScript hydration.",
      },
      {
        type: "code",
        language: "javascript",
        text: `export const metadata = {
  title: "Aftab Ali | Full Stack Developer",
  description: "React Native and Next.js developer portfolio",
  openGraph: {
    title: "Aftab Ali",
    description: "Mobile & web developer",
    url: "https://theaftabali.com",
  },
};`,
      },
      {
        type: "heading",
        text: "Performance = SEO",
      },
      {
        type: "paragraph",
        text: "Google's Core Web Vitals matter. Use next/image for optimized images, next/font for zero-layout-shift fonts, and dynamic imports for heavy client components. A fast site ranks better and converts more users.",
      },
    ],
  },
  {
    slug: "react-hooks-complete-guide",
    title: "React Hooks: A Complete Guide for Modern Development",
    excerpt:
      "Master useState, useEffect, useContext, useMemo, useCallback, and custom hooks to write cleaner React applications.",
    date: "2026-03-20",
    readTime: "11 min read",
    tags: ["React", "JavaScript", "Web"],
    image: reactHooksImg,
    gradient: "linear-gradient(135deg, #61dafb 0%, #20232a 100%)",
    content: [
      {
        type: "paragraph",
        text: "Hooks changed how we write React. They let you use state and lifecycle features in function components without classes — and they compose beautifully into reusable custom hooks.",
      },
      {
        type: "heading",
        text: "Core Hooks",
      },
      {
        type: "list",
        items: [
          "useState — local component state",
          "useEffect — side effects, subscriptions, and data fetching",
          "useContext — consume context without prop drilling",
          "useRef — mutable values and DOM references",
          "useMemo / useCallback — memoize expensive computations and callbacks",
        ],
      },
      {
        type: "heading",
        text: "useEffect Done Right",
      },
      {
        type: "paragraph",
        text: "The most misused hook in React. Always define your dependency array explicitly. Clean up subscriptions in the return function. Don't use useEffect for derived state — compute it during render instead.",
      },
      {
        type: "code",
        language: "javascript",
        text: `useEffect(() => {
  const controller = new AbortController();

  async function fetchData() {
    const res = await fetch("/api/user", { signal: controller.signal });
    const data = await res.json();
    setUser(data);
  }

  fetchData();
  return () => controller.abort();
}, [userId]);`,
      },
      {
        type: "heading",
        text: "Custom Hooks",
      },
      {
        type: "paragraph",
        text: "Extract repeated logic into custom hooks like useFetch, useLocalStorage, or useDebounce. If it uses hooks and returns state or handlers, it's a custom hook. Prefix with 'use' and keep them focused on one responsibility.",
      },
    ],
  },
  {
    slug: "react-state-management-patterns",
    title: "React State Management: Context, Redux & Zustand Compared",
    excerpt:
      "When to use local state, Context API, Redux Toolkit, or Zustand — and how to pick the right tool for your React app.",
    date: "2026-03-14",
    readTime: "9 min read",
    tags: ["React", "Architecture", "Web"],
    image: reactStateImg,
    gradient: "linear-gradient(135deg, #764abc 0%, #61dafb 100%)",
    content: [
      {
        type: "paragraph",
        text: "State management is one of the most debated topics in React. The truth is there's no single best solution — it depends on your app's size, team, and data flow complexity.",
      },
      {
        type: "heading",
        text: "Start Simple: Local State",
      },
      {
        type: "paragraph",
        text: "useState and lifting state up handle 80% of cases. Don't reach for a global store until you feel real pain — prop drilling through 4+ levels, or the same data needed in distant branches of the tree.",
      },
      {
        type: "heading",
        text: "React Context",
      },
      {
        type: "paragraph",
        text: "Great for theme, auth user, locale, and other low-frequency global data. Avoid putting fast-changing values in Context — every update re-renders all consumers. Split contexts by domain to limit blast radius.",
      },
      {
        type: "heading",
        text: "Redux Toolkit vs Zustand",
      },
      {
        type: "list",
        items: [
          "Redux Toolkit — best for large teams, complex workflows, time-travel debugging",
          "Zustand — minimal boilerplate, great DX, perfect for mid-size apps",
          "Both work with React Native and Next.js",
          "Consider TanStack Query for server state regardless of client state choice",
        ],
      },
      {
        type: "paragraph",
        text: "My recommendation: local state first, Context for auth/theme, TanStack Query for API data, and Zustand or Redux only when client global state genuinely simplifies your architecture.",
      },
    ],
  },
  {
    slug: "android-development-kotlin-guide",
    title: "Android Development with Kotlin: A Beginner's Guide",
    excerpt:
      "Get started building native Android apps with Kotlin, Jetpack Compose, and Android Studio — from setup to your first release.",
    date: "2026-02-28",
    readTime: "10 min read",
    tags: ["Android", "Kotlin", "Mobile"],
    image: androidDevImg,
    gradient: "linear-gradient(135deg, #3ddc84 0%, #073042 100%)",
    content: [
      {
        type: "paragraph",
        text: "Android powers billions of devices worldwide. Kotlin is the official language for Android development, and Jetpack Compose has made building UIs faster and more declarative than the old XML layout system.",
      },
      {
        type: "heading",
        text: "Setting Up Android Studio",
      },
      {
        type: "list",
        items: [
          "Download Android Studio from developer.android.com",
          "Install the Android SDK and create a virtual device (AVD)",
          "Choose 'Empty Activity' with Jetpack Compose when creating a new project",
          "Enable Kotlin DSL for Gradle in new projects",
        ],
      },
      {
        type: "heading",
        text: "Your First Compose Screen",
      },
      {
        type: "code",
        language: "kotlin",
        text: `@Composable
fun GreetingScreen(name: String) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.dp),
        verticalArrangement = Arrangement.Center
    ) {
        Text(
            text = "Hello, $name!",
            style = MaterialTheme.typography.headlineMedium
        )
    }
}`,
      },
      {
        type: "heading",
        text: "Key Android Concepts",
      },
      {
        type: "list",
        items: [
          "Activities and the activity lifecycle",
          "Jetpack Compose for declarative UI",
          "ViewModel + StateFlow for state management",
          "Retrofit or Ktor for networking",
          "Room database for local persistence",
        ],
      },
      {
        type: "paragraph",
        text: "Test on real devices early. Emulators are fine for layout work, but performance, sensors, and notifications behave differently on hardware.",
      },
    ],
  },
  {
    slug: "publish-app-google-play-store",
    title: "How to Publish Your App on Google Play Store",
    excerpt:
      "A step-by-step guide to preparing, signing, and submitting your Android app to the Google Play Store.",
    date: "2026-02-18",
    readTime: "8 min read",
    tags: ["Android", "Play Store", "Mobile"],
    image: androidPlayStoreImg,
    gradient: "linear-gradient(135deg, #34a853 0%, #1a1a2e 100%)",
    content: [
      {
        type: "paragraph",
        text: "Publishing to Google Play is the final step that turns your Android project into a product real users can download. The process has gotten smoother over the years, but there are still important steps you can't skip.",
      },
      {
        type: "heading",
        text: "Pre-Launch Checklist",
      },
      {
        type: "list",
        items: [
          "Generate a signed release APK or AAB (Android App Bundle)",
          "Set versionCode and versionName in build.gradle",
          "Test on multiple devices and Android versions",
          "Prepare screenshots, feature graphic, and app description",
          "Write a privacy policy if your app collects user data",
        ],
      },
      {
        type: "heading",
        text: "Signing Your App",
      },
      {
        type: "paragraph",
        text: "Create a keystore file and keep it safe — losing it means you can never update your app. In Android Studio, use Build > Generate Signed Bundle/APK. Google Play App Signing can manage your production key for added security.",
      },
      {
        type: "heading",
        text: "Play Console Submission",
      },
      {
        type: "list",
        items: [
          "Create a developer account ($25 one-time fee)",
          "Fill in store listing: title, description, screenshots, category",
          "Complete the content rating questionnaire",
          "Set up pricing and countries",
          "Upload your AAB to Internal Testing first, then Production",
        ],
      },
      {
        type: "paragraph",
        text: "Review times are usually 1–3 days. Start with internal testing to catch issues before going public. Monitor crash reports and reviews closely in the first week after launch.",
      },
    ],
  },
  {
    slug: "ios-development-swift-guide",
    title: "iOS Development with Swift & SwiftUI: Getting Started",
    excerpt:
      "Learn the fundamentals of building native iOS apps with Swift, SwiftUI, and Xcode — from project setup to App Store readiness.",
    date: "2026-02-10",
    readTime: "10 min read",
    tags: ["iOS", "Swift", "Mobile"],
    image: iosDevImg,
    gradient: "linear-gradient(135deg, #007aff 0%, #1a1a2e 100%)",
    content: [
      {
        type: "paragraph",
        text: "iOS development offers premium user experiences and strong monetization opportunities. Swift is Apple's modern language, and SwiftUI has become the standard for building interfaces across iPhone, iPad, and even Apple Watch.",
      },
      {
        type: "heading",
        text: "Xcode & Project Setup",
      },
      {
        type: "list",
        items: [
          "Install Xcode from the Mac App Store (macOS required)",
          "Create a new project with SwiftUI App template",
          "Understand the project structure: App, Views, Models, Assets",
          "Use Swift Package Manager for third-party dependencies",
        ],
      },
      {
        type: "heading",
        text: "SwiftUI Basics",
      },
      {
        type: "code",
        language: "swift",
        text: `struct ContentView: View {
    @State private var name = ""

    var body: some View {
        VStack(spacing: 16) {
            Text("Hello, \\(name.isEmpty ? "World" : name)!")
                .font(.largeTitle)
            TextField("Enter your name", text: $name)
                .textFieldStyle(.roundedBorder)
                .padding(.horizontal)
        }
        .padding()
    }
}`,
      },
      {
        type: "heading",
        text: "Essential iOS Patterns",
      },
      {
        type: "list",
        items: [
          "@State and @Observable for local and shared state",
          "NavigationStack for screen-to-screen flows",
          "URLSession or Alamofire for networking",
          "Core Data or SwiftData for persistence",
          "Human Interface Guidelines for design consistency",
        ],
      },
      {
        type: "paragraph",
        text: "Apple's review process is stricter than Google's. Follow the HIG, handle permissions gracefully, and test on multiple iPhone sizes including the latest Pro models.",
      },
    ],
  },
  {
    slug: "publish-app-apple-app-store",
    title: "How to Publish Your App on the Apple App Store",
    excerpt:
      "Everything you need to know about App Store Connect, TestFlight, review guidelines, and submitting your iOS app for approval.",
    date: "2026-01-25",
    readTime: "9 min read",
    tags: ["iOS", "App Store", "Mobile"],
    image: iosAppStoreImg,
    gradient: "linear-gradient(135deg, #5856d6 0%, #007aff 100%)",
    content: [
      {
        type: "paragraph",
        text: "Publishing to the App Store is more rigorous than Google Play, but the process is well-documented. Apple reviews every submission for quality, privacy, and guideline compliance before it goes live.",
      },
      {
        type: "heading",
        text: "Before You Submit",
      },
      {
        type: "list",
        items: [
          "Enroll in the Apple Developer Program ($99/year)",
          "Create an App ID and provisioning profiles in the developer portal",
          "Archive your app in Xcode (Product > Archive)",
          "Prepare app icons in all required sizes (use App Icon Generator tools)",
          "Write App Store description, keywords, and privacy policy URL",
        ],
      },
      {
        type: "heading",
        text: "TestFlight Beta Testing",
      },
      {
        type: "paragraph",
        text: "Always run a TestFlight beta before public release. Invite internal testers first, then external testers (up to 10,000). TestFlight builds expire after 90 days, so keep uploading fresh builds during long beta cycles.",
      },
      {
        type: "heading",
        text: "App Review Tips",
      },
      {
        type: "list",
        items: [
          "Provide demo login credentials if your app requires authentication",
          "Explain non-obvious features in the Review Notes field",
          "Ensure all permission strings (camera, location) have clear explanations",
          "Avoid placeholder content or broken links in the review build",
          "Respond promptly if Apple requests changes — you have limited resubmission windows",
        ],
      },
      {
        type: "paragraph",
        text: "Review typically takes 24–48 hours. Rejections are common on first submission — read the feedback carefully, fix the issues, and resubmit. Most apps get approved within 1–2 rounds.",
      },
    ],
  },
];

export function getBlogBySlug(slug) {
  return blogs.find((blog) => blog.slug === slug);
}
